<template>
  <UiCard>
    <div class="lh__bar">
      <!-- El buscador NO se pliega. Es la única cosa de esta cabecera que se
           usa en cada visita, y esconderla convertiría un gesto en dos. -->
      <div v-if="$slots.search" class="lh__search"><slot name="search" /></div>

      <!-- Qué se está mirando, cuando lo que se mira no es todo. Con el panel
           cerrado es lo único que da sentido a las cifras de debajo y a las
           filas de más abajo: seis pendientes no significan lo mismo en una
           semana que en un trimestre. -->
      <p v-if="summary" class="lh__summary">{{ summary }}</p>

      <UiButton
        v-if="$slots.filters"
        class="lh__toggle"
        variant="secondary"
        :aria-expanded="filtersOpen"
        @click="filtersOpen = !filtersOpen"
      >
        <template #icon><Chevron :open="filtersOpen" /></template>
        {{ uiLabel('filters') }}
        <!-- Cuántos hay puestos. Un filtro activo que no se ve es una lista que
             miente, y con el panel cerrado este número es lo único que lo
             cuenta. Las pantallas cuyo filtro SIEMPRE tiene valor —un rango de
             fechas por defecto, por ejemplo— no pasan `active` y usan
             `summary`: un contador que nunca baja a cero se deja de mirar. -->
        <span v-if="active" class="lh__count">{{ active }}</span>
      </UiButton>
    </div>

    <!-- Lo que no es ni filtro ni cifra va aquí, y va DEBAJO DE LA BARRA y no
         al final: lo que llega por este hueco son unas migas de pan o un aviso
         de por qué la lista es corta, y las dos cosas describen lo que se está
         mirando. Al final de la tarjeta, después de un panel de filtros que
         casi siempre está cerrado, describirían la lista desde detrás. -->
    <div v-if="$slots.default" class="lh__extra"><slot /></div>

    <div v-if="$slots.filters && filtersOpen" class="lh__filters"><slot name="filters" /></div>

    <!-- ── El resumen ──────────────────────────────────────────────────────
         Una línea de cifras, y los azulejos enteros para quien los quiera.
         Cuatro UiStat son 300 px de pantalla antes de la primera fila, y en un
         móvil eso es la lista entera fuera del pliegue — pero esconder que hay
         cuatro jornadas sin cerrar sería esconder el trabajo pendiente. Así
         que siguen estando los cuatro números; en 24 px en vez de en 300.

         Es un <button> entero y no un texto con una flecha al lado: el área
         que se pulsa tiene que ser la que se ve, y en un teléfono una flecha
         de 14 px no la acierta nadie. -->
    <button
      v-if="stats.length"
      type="button"
      class="lh__totals"
      :aria-expanded="statsOpen"
      @click="statsOpen = !statsOpen"
    >
      <span v-if="statsOpen" class="lh__totals-title">{{ uiLabel('summary') }}</span>
      <template v-else>
        <span v-for="stat in stats" :key="stat.key ?? stat.label" class="lh__figure">
          <strong class="lh__value" :class="stat.tone ? `lh__value--${stat.tone}` : null">
            {{ stat.value }}
          </strong>
          {{ stat.label }}
        </span>
      </template>
      <Chevron :open="statsOpen" class="lh__totals-chevron" />
    </button>

    <div v-if="stats.length && statsOpen" class="lh__stats">
      <UiStat
        v-for="stat in stats" :key="stat.key ?? stat.label"
        :label="stat.label" :value="stat.value" :tone="stat.tone"
        :loading="loading"
      />
    </div>

  </UiCard>
</template>

<script setup>
import { h, ref } from 'vue'

import UiCard from './UiCard.vue'
import UiButton from './UiButton.vue'
import UiStat from './UiStat.vue'
import { uiLabel } from '../utils/labels'

/**
 * La cabecera de una pantalla de lista: buscador, filtros y resumen.
 *
 * Existe porque las cuarenta y tantas listas del ERP tenían la misma cabecera
 * copiada —tres campos de filtro siempre abiertos y una fila de azulejos de
 * KPI— y esa cabecera se comía media pantalla de móvil antes del primer
 * registro. Una pantalla de lista es para leer la lista.
 *
 * Lo que hace, y nada más: el buscador se queda fuera y siempre visible; los
 * filtros y el resumen se pliegan, cerrados al entrar, y se abren cuando
 * alguien pregunta otra cosa. Los campos y las cifras los pone quien la usa —
 * este componente decide la FORMA, no el contenido.
 *
 * El contenido que no cabe en ninguno de los dos huecos va al slot por
 * defecto, justo debajo de la barra (unas migas de pan, un aviso de por qué la
 * lista es corta).
 */
defineProps({
  /** Qué se está mirando ahora mismo, para la línea de contexto. */
  summary: { type: String, default: '' },
  /** Cuántos filtros hay puestos. 0 o ausente = sin contador. */
  active: { type: Number, default: 0 },
  /** [{ key?, label, value, tone? }] — vacío = sin resumen. */
  stats: { type: Array, default: () => [] },
  /** Se pasa a los azulejos: mientras carga dibujan su propio esqueleto. */
  loading: Boolean,
})

const filtersOpen = ref(false)
const statsOpen = ref(false)

defineExpose({ filtersOpen, statsOpen })

/** El mismo galón que dibuja UiSelect, girando igual. */
const Chevron = (props) => h('svg', {
  class: ['lh__chevron', props.open ? 'lh__chevron--on' : null],
  width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', 'stroke-width': 2,
  'stroke-linecap': 'round', 'stroke-linejoin': 'round',
  'aria-hidden': 'true',
}, [h('path', { d: 'M6 9l6 6 6-6' })])
Chevron.props = { open: Boolean }
</script>

<style scoped>
.lh__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
}

/* Base de 10rem y no de 18: con 18 el buscador se lleva la línea entera en un
   móvil y tira el botón de filtros abajo, que es justo el hueco que esta
   cabecera viene a recuperar. Sigue siendo una base, no un ancho. */
.lh__search { flex: 1 1 10rem; min-width: 0; }

.lh__summary {
  margin: 0;
  min-width: 0;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-soft);
}

/* Empuja el botón al extremo cuando es lo único que hay en la línea. */
.lh__toggle { margin-left: auto; }

.lh__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  margin-left: var(--space-2);
  padding: 0 5px;
  border-radius: var(--radius-full);
  background: var(--color-primary);
  color: var(--color-text-on-primary);
  font-size: var(--text-xs);
  font-weight: 600;
}

.lh__chevron { transition: transform var(--fast); }
.lh__chevron--on { transform: rotate(180deg); }

.lh__extra { margin-top: var(--space-3); }

.lh__filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(170px, 100%), 1fr));
  gap: var(--space-4);
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-border);
}

.lh__totals {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-4);
  width: 100%;
  margin-top: var(--space-3);
  /* 31 px de alto medidos en un móvil, y es un BOTÓN: pliega y despliega el
     resumen. Va justo encima de la primera fila de la lista, así que fallarlo
     abre un registro. El relleno de abajo se añade solo con un dedo, para no
     separar la tira de los números en un escritorio. */
  padding: var(--space-2) 0 0;
  border: none;
  border-top: 1px solid var(--color-border);
  background: none;
  font: inherit;
  font-size: var(--text-sm);
  color: var(--color-text-soft);
  text-align: left;
  cursor: pointer;
}

/* Y el relleno que prometía el comentario de arriba: solo con un dedo, para no
   separar la tira de los números en un escritorio, donde el ratón acierta. */
@media (pointer: coarse) {
  .lh__totals { min-height: 44px; padding-bottom: var(--space-2); }
}

.lh__figure { display: inline-flex; align-items: baseline; gap: var(--space-2); }
.lh__value { font-size: var(--text-md); font-variant-numeric: tabular-nums; }
.lh__value--primary { color: var(--color-primary); }
.lh__value--success { color: var(--color-success); }
.lh__value--warning { color: var(--color-warning); }
.lh__value--danger  { color: var(--color-danger); }
.lh__value--info    { color: var(--color-info); }
.lh__totals-title { font-weight: 600; }

/* Al extremo, esté la línea llena de cifras o solo con la palabra «Resumen». */
.lh__totals-chevron { margin-left: auto; }

.lh__stats {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin-top: var(--space-3);
}
</style>
